import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Download, Paperclip, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import StatusPill from "@/components/StatusPill";
import { formatDateTime, formatRole, STATUS_LABEL } from "@/lib/utils";
import { ADMIN } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { ParameterReviewTable, RawDataSourceReview } from "@/components/jobs/ParameterReviewTable";

const ALLOWED_TRANSITIONS = {
  submitted: ["under_review", "rejected"],
  under_review: ["revision", "queued", "rejected"],
  resubmitted: ["under_review"],
  queued: ["running"],
  running: ["success", "failed"],
  success: ["quality_review"],
  quality_review: ["completed", "reprocessing"],
  failed: ["reprocessing", "closed"],
  reprocessing: ["running"],
  revision: [],
  rejected: [],
  completed: [],
  closed: [],
};

const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

function downloadCsv(filename, headers, rows) {
  const csv = [headers.map(csvCell).join(","), ...rows.map((row) => row.map(csvCell).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function AdminJobsPage({ workstation = false }) {
  const { user: me } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [techs, setTechs] = useState([]);
  // Result-attach form
  const [reportUrl, setReportUrl] = useState("");
  const [files, setFiles] = useState([]);      // [{name, url}]
  const [fileName, setFileName] = useState("");
  const [fileUrl, setFileUrl] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/admin/jobs?${params.toString()}`).then(({ data }) => setJobs(data));
  }, [status, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/admin/technical-admins").then(({ data }) => setTechs(data)).catch(() => { });
  }, []);

  // When selection changes, prime the result form with existing values
  useEffect(() => {
    if (selected?.result) {
      setReportUrl(selected.result.report_url || "");
      setFiles(selected.result.file_urls || []);
    } else {
      setReportUrl(""); setFiles([]);
    }
    setFileName(""); setFileUrl("");
  }, [selected?.id]);

  const canTech = me?.role === "super_admin" || me?.role === "analyst_admin";
  const manifestRows = selected?.parameters?.manifest_file_rows || [];
  const metadataRows = selected?.parameters?.metadata_file_rows || [];

  const doUpdateStatus = async () => {
    if (!selected || !newStatus) return;
    setSaving(true);
    try {
      await api.post(`/jobs/${selected.id}/status`, { new_status: newStatus, note });
      toast.success("Job status updated.");
      setNote(""); setNewStatus("");
      await load();
      const { data } = await api.get(`/jobs/${selected.id}`);
      setSelected(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const assign = async (assignee_id) => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/jobs/${selected.id}/assign`, { assignee_id });
      toast.success("Assigned.");
      await load();
      const { data } = await api.get(`/jobs/${selected.id}`);
      setSelected(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const addFile = () => {
    if (!fileUrl.trim()) return;
    setFiles([...files, { name: fileName.trim() || fileUrl.trim(), url: fileUrl.trim() }]);
    setFileName(""); setFileUrl("");
  };

  const saveResult = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.post(`/jobs/${selected.id}/result`, {
        report_url: reportUrl.trim(),
        file_urls: files,
      });
      toast.success("Result attached to job.");
      const { data } = await api.get(`/jobs/${selected.id}`);
      setSelected(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!jobs) return <LoadingScreen />;

  const allowedNext = selected ? (ALLOWED_TRANSITIONS[selected.status] || []) : [];

  return (
    <div className="grid h-[calc(100vh-7rem)] grid-cols-1 gap-6 overflow-hidden lg:grid-cols-3">
      <div className="lg:col-span-2 sqit-card flex flex-col overflow-hidden">
        <div className="p-4 border-b border-[#E5E7EB] flex flex-col md:flex-row gap-3">
          <input
            data-testid="admin-jobs-search"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by job code, email or project…"
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
          />
          <select
            data-testid="admin-jobs-status-filter"
            value={status} onChange={(e) => setStatus(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm" data-testid={ADMIN.jobsTable}>
            <thead className="bg-gray-50 border-b border-[#E5E7EB]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Job</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Module</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assignee</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr
                  key={j.id}
                  data-testid={`admin-job-row-${j.id}`}
                  onClick={() => setSelected(j)}
                  className={`border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${selected?.id === j.id ? "bg-[#EAF4E3]/40" : ""}`}
                >
                  <td className="px-6 py-3">
                    <div className="font-semibold text-gray-900">{j.job_code}</div>
                    <div className="text-xs text-gray-500">{j.project_name}</div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="text-gray-900">{j.user_name}</div>
                    <div className="text-xs text-gray-500">{j.user_email}</div>
                  </td>
                  <td className="px-6 py-3">{j.module_name}</td>
                  <td className="px-6 py-3"><StatusPill status={j.status} /></td>
                  <td className="px-6 py-3 text-xs text-gray-500">{j.assignee_name || "—"}</td>
                  <td className="px-6 py-3 text-xs text-gray-500">{formatDateTime(j.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {jobs.length === 0 && <div className="p-8 text-center text-gray-500">No jobs match the filters.</div>}
        </div>
      </div>

      <div className="sqit-card flex h-full flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-6">
          {selected ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg font-semibold text-gray-900">{selected.job_code}</h3>
                  <p className="text-xs text-gray-500">{selected.project_name}</p>
                </div>
                <StatusPill status={selected.status} />
              </div>
              <dl className="mt-5 space-y-2 text-sm">
                <div><dt className="text-xs text-gray-500 uppercase tracking-wider">User</dt><dd>{selected.user_email}</dd></div>
                <div><dt className="text-xs text-gray-500 uppercase tracking-wider">Project</dt><dd>{selected.project_name || "—"}</dd></div>
                <div><dt className="text-xs text-gray-500 uppercase tracking-wider">Samples</dt><dd>{selected.number_of_samples}</dd></div>
                <div><dt className="text-xs text-gray-500 uppercase tracking-wider">Credits</dt><dd>{selected.credits_required} (frozen {selected.credits_frozen})</dd></div>
              </dl>

              <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
                <div className="text-xs font-semibold text-gray-700 mb-2">Submission details</div>
                {selected.short_notes && (
                  <div className="mb-3">
                    <div className="text-[11px] uppercase tracking-wider text-gray-500">Notes</div>
                    <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{selected.short_notes}</p>
                  </div>
                )}
                <div className="mb-3">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">Raw Data Source</div>
                  <div className="mt-1"><RawDataSourceReview source={selected.raw_data_source} legacyUrls={selected.sample_urls} showPassword /></div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">Parameters</div>
                  <div className="mt-2"><ParameterReviewTable parameters={selected.parameters} emptyMessage="No module parameters saved." /></div>
                  {(manifestRows.length > 0 || metadataRows.length > 0) && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {manifestRows.length > 0 && (
                        <button
                          type="button"
                          onClick={() => downloadCsv(`${selected.job_code}_manifest.csv`, ["Sample ID", "Forward Absolute Path", "Reverse Absolute Path"], manifestRows.map((row) => [row.sample_id, row.forward_absolute_path, row.reverse_absolute_path]))}
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#538135]/30 px-3 py-1.5 text-xs font-semibold text-[#09582D] hover:border-[#538135]"
                        >
                          <Download size={12} /> Download Manifest
                        </button>
                      )}
                      {metadataRows.length > 0 && (
                        <button
                          type="button"
                          onClick={() => downloadCsv(`${selected.job_code}_metadata.csv`, ["ID", "Status"], metadataRows.map((row) => [row.id, row.status]))}
                          className="inline-flex items-center gap-1.5 rounded-md border border-[#538135]/30 px-3 py-1.5 text-xs font-semibold text-[#09582D] hover:border-[#538135]"
                        >
                          <Download size={12} /> Download Metadata
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
                <label className="text-xs font-semibold text-gray-700">Update status</label>
                <select
                  data-testid={ADMIN.jobStatusSelect}
                  value={newStatus} onChange={(e) => setNewStatus(e.target.value)}
                  disabled={!canTech || allowedNext.length === 0}
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                >
                  <option value="">Select…</option>
                  {allowedNext.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
                <textarea
                  rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note (visible to user)"
                  data-testid="admin-job-status-note"
                  className="mt-2 w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
                <button
                  disabled={!canTech || !newStatus || saving}
                  onClick={doUpdateStatus}
                  data-testid={ADMIN.jobStatusSubmit}
                  className="mt-2 w-full bg-[#538135] text-white hover:bg-[#09582D] rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60"
                >
                  Update status
                </button>

                <div className="mt-5">
                  <label className="text-xs font-semibold text-gray-700">Assign to</label>
                  <select
                    data-testid="admin-job-assignee"
                    value={selected.assignee_id || ""}
                    onChange={(e) => assign(e.target.value || null)}
                    disabled={!canTech || saving}
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                  >
                    <option value="">— Unassigned —</option>
                    {techs.map((t) => (
                      <option key={t.id} value={t.id}>{`${t.full_name} (${formatRole(t.role)})`}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
                <div className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1.5">
                  <Paperclip size={12} /> Attach results
                </div>
                <label className="block text-[11px] text-gray-500 mb-1">Main report URL</label>
                <input
                  data-testid="admin-job-report-url"
                  value={reportUrl} onChange={(e) => setReportUrl(e.target.value)}
                  placeholder="https://…/report.html"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
                />
                <div className="text-[11px] text-gray-500 mb-1">Additional files</div>
                {files.length > 0 && (
                  <ul className="mb-2 space-y-1">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between text-xs bg-[#F8FAF7] border border-[#E5E7EB] rounded px-2 py-1.5">
                        <span className="truncate">{f.name}</span>
                        <button onClick={() => setFiles(files.filter((_, j) => j !== i))}
                          className="text-gray-400 hover:text-[#DC2626]">
                          <Trash2 size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  <input
                    data-testid="admin-job-file-name" value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="Label"
                    className="border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                  />
                  <input
                    data-testid="admin-job-file-url" value={fileUrl}
                    onChange={(e) => setFileUrl(e.target.value)}
                    placeholder="https://…"
                    className="border border-gray-300 rounded-md px-2 py-1.5 text-xs"
                  />
                </div>
                <button
                  onClick={addFile}
                  data-testid="admin-job-file-add"
                  className="w-full mb-2 border border-dashed border-gray-300 text-xs rounded-md px-2 py-1.5 text-gray-600 hover:border-[#538135] hover:text-[#09582D]"
                >
                  <Plus size={11} className="inline mr-1" /> Add file
                </button>
                <button
                  onClick={saveResult}
                  disabled={!canTech || saving}
                  data-testid="admin-job-result-save"
                  className="w-full bg-[#538135] text-white hover:bg-[#09582D] rounded-md px-4 py-2 text-sm font-semibold disabled:opacity-60"
                >
                  Save attached results
                </button>
              </div>

              <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
                <div className="text-xs font-semibold text-gray-700 mb-2">Timeline</div>
                <ul className="space-y-2 max-h-56 overflow-y-auto">
                  {(selected.history || []).slice().reverse().map((h, i) => (
                    <li key={i} className="text-xs">
                      <span className="font-semibold text-gray-900">{STATUS_LABEL[h.status] || h.status}</span>
                      <span className="text-gray-500 ml-2">{formatDateTime(h.at)}</span>
                      {h.note && <div className="text-gray-600 mt-0.5">{h.note}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">Select a job to see details and take action.</p>
          )}
        </div>
      </div>
    </div>
  );
}